import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Drawer,
} from '@mui/material';
import Cancel from '../../assets/images/Cancel.svg';
import { useTheme } from '@mui/material/styles';
import { getCitationData } from '../../api/api';

interface Reference {
  id: number;
  fileName: string;
  pageNumber: number;
  fileId: string;
  text: string;
}

interface CitationPanelProps {
  open: boolean;
  onClose: () => void;
  selectedReference: Reference | null;
}

const CitationPanel: React.FC<CitationPanelProps> = ({ open, onClose, selectedReference }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [citationUrl, setCitationUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchCitationUrl = async () => {
      if (selectedReference) {
        setLoading(true);
        try {
          const response = await getCitationData(
            selectedReference.fileId,
            selectedReference.pageNumber.toString()
          );
          
          if (response && response.citation_url && response.citation_url.length > 0) {
            setCitationUrl(response.citation_url);
          } else {
            console.error('No citation URL found in response');
          }
        } catch (error) {
          console.error('Error fetching citation URL:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (open && selectedReference) {
      fetchCitationUrl();
    } else {
      setCitationUrl(null);
    }
  }, [open, selectedReference]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: '50vw',
          boxSizing: 'border-box',
          top: '64px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.paper,
      }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Box>
            <Typography variant="h6">Reference</Typography>
            {selectedReference && (
              <Typography variant="body2" color="text.secondary">
                {selectedReference.fileName} - Page {selectedReference.pageNumber}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <img src={Cancel} alt="Close" style={{ width: 20, height: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ 
          flex: 1,
          p: 2,
          overflowY: 'auto',
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
              <CircularProgress />
            </Box>
          ) : citationUrl ? (
            <Box sx={{ height: '100%' }}>
              <iframe
                src={citationUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={selectedReference?.fileName || "Contract PDF"}
              />
            </Box>
          ) : (
            <Typography color="error">
              Unable to load the reference document. Please try again later.
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default CitationPanel;